import { Link } from "react-router-dom";
import { ArrowRight, Package, Users, Palette, Truck } from "lucide-react";
import { useClinicAuth } from "../context/ClinicAuthContext";

export default function ClinicsLandingPage() {
  const { clinic } = useClinicAuth();
  return (
    <div>
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="eyebrow">For Clinics & Surgeons</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl mt-6 leading-[1.02] tracking-tight">
            Give your patients a <span className="serif-italic text-[#B85C56]">better recovery</span> — before you even see them again.
          </h1>
          <p className="mt-8 max-w-lg text-lg text-[#6B5757] leading-relaxed">
            ALTA Recovery for Clinics lets surgeons order patient-specific, procedure-configured recovery systems delivered before surgery — with your name, your tagline, your voice.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            {clinic ? (
              <Link to="/clinics/dashboard" data-testid="clinics-cta-dashboard" className="btn-primary inline-flex items-center gap-3">
                Open Dashboard <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <>
                <Link to="/clinics/login" data-testid="clinics-cta-login" className="btn-primary inline-flex items-center gap-3">
                  Sign In <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/clinics/register" data-testid="clinics-cta-register" className="btn-outline">
                  Apply for Access
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] overflow-hidden rounded-xl">
            <img src="/generated/hero_facelift.png" alt="Clinic recovery" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white border border-[#F0DFD0] p-6 rounded-lg shadow-[0_12px_36px_-6px_rgba(42,31,31,0.12)] hidden md:block">
            <div className="eyebrow">Delivered</div>
            <div className="font-serif text-3xl mt-1">3–5 days</div>
            <div className="text-xs text-[#6B5757] mt-1">before your patient's surgery</div>
          </div>
        </div>
      </section>

      <section className="bg-[#2A1F1F] text-[#F0DFD0] py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="eyebrow">Why Clinics Choose ALTA</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-6xl mt-6 leading-[1.05]">
              Patient-specific.<br /><span className="serif-italic text-[#B89B66]">Procedure-perfect.</span>
            </h2>
            <p className="mt-8 text-[#E5C1AF] leading-relaxed max-w-md">
              Configure the exact ALTA system for each patient's procedure and surgery date. We handle logistics. You keep the relationship.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-6">
            {[
              { icon: Package, title: "Patient-Specific", desc: "One order per patient, configured to their exact procedure." },
              { icon: Users, title: "Your Voice", desc: "Add surgeon notes so patients hear your protocol." },
              { icon: Palette, title: "Co-Branded", desc: "Your clinic name and colour on the patient's tracking page." },
              { icon: Truck, title: "Pre-Op Delivery", desc: "Arrives before surgery. Signature not required." },
            ].map((f) => (
              <div key={f.title} className="border border-[#3A2A2A] p-6 rounded-lg">
                <f.icon className="w-6 h-6 text-[#B89B66]" strokeWidth={1.2} />
                <h4 className="font-serif text-lg mt-4 text-white">{f.title}</h4>
                <p className="text-xs text-[#A08D8D] mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-24 text-center">
        <div className="eyebrow">The Workflow</div>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-4 tracking-tight">Order in under two minutes.</h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-8 text-left">
          {[
            { n: "01", title: "Sign In", desc: "Access your ALTA clinic account." },
            { n: "02", title: "Add Patient", desc: "Name, surgery date, procedure, notes." },
            { n: "03", title: "Configure", desc: "Swap brands to match your protocol." },
            { n: "04", title: "We Deliver", desc: "Pre-op, discreet, branded to your clinic." },
          ].map((s) => (
            <div key={s.n} className="border-t border-[#2A1F1F] pt-6">
              <div className="font-mono text-xs tracking-widest text-[#B85C56]">{s.n}</div>
              <h3 className="font-serif text-xl mt-4">{s.title}</h3>
              <p className="text-sm text-[#6B5757] mt-2 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
